import React from 'react';
import { Mail, MapPin, Phone, MessageCircle, Clock } from 'lucide-react';
import BlurFade from '../components/primitives/BlurFade';
import Seo from '../components/Seo';
import { useLang } from '../LangContext';
import { company } from '../i18n';

const waHref = (msg) => `https://wa.me/${company.whatsapp}?text=${encodeURIComponent(msg || '')}`;
const telHref = (p) => `tel:${(p || '').replace(/\s/g, '')}`;

export default function ContactPage() {
  const { lang } = useLang();
  const phone = lang === 'de' ? company.phoneDE : company.phoneNL;

  const labels = lang === 'de' ? {
    eyebrow: 'Kontakt',
    title: 'Schreib uns. Wir antworten persönlich.',
    sub: 'Wir sind ein kleines Team — keine Hotline. Schreib uns auf WhatsApp, ruf an oder schick eine E-Mail. Antwort meist innerhalb eines Werktags.',
    hours: 'Öffnungszeiten',
    address: 'Anschrift',
    wa: 'WhatsApp', email: 'E-Mail', call: 'Anrufen',
  } : {
    eyebrow: 'Contact',
    title: 'Schrijf ons. Wij antwoorden persoonlijk.',
    sub: 'Wij zijn een klein team — geen callcenter. Stuur ons een appje, bel ons of stuur een e-mail. Reactie meestal binnen één werkdag.',
    hours: 'Openingstijden',
    address: 'Adres',
    wa: 'WhatsApp', email: 'E-mail', call: 'Bel ons',
  };

  const hrs = company.hours[lang === 'de' ? 'de' : 'nl'];

  return (
    <article data-testid="contact-page" className="relative bg-[#FAF8F2]">
      <Seo title={labels.title} description={labels.sub} path="/contact" lang={lang} />
      <section className="pt-28 sm:pt-36 pb-16 lg:pb-24">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
          <BlurFade>
            <p className="text-[11.5px] uppercase tracking-[0.22em] text-[#F26B1A] font-medium mb-6">{labels.eyebrow}</p>
          </BlurFade>
          <BlurFade delay={0.05}>
            <h1 className="font-display text-[#1F1D1A] text-[40px] sm:text-[56px] lg:text-[80px] leading-[1.02] max-w-3xl">{labels.title}</h1>
          </BlurFade>
          <BlurFade delay={0.15}>
            <p className="mt-7 text-[#5C5852] text-[16px] lg:text-[18px] leading-relaxed max-w-2xl">{labels.sub}</p>
          </BlurFade>
        </div>
      </section>

      <section className="bg-[#F2EEDF] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <a href={waHref(lang === 'de' ? 'Hallo Horrenmaker, ich habe eine Frage.' : 'Hallo Horrenmaker, ik heb een vraag.')}
            target="_blank" rel="noreferrer"
            className="group block rounded-[22px] bg-white border border-[#E5E0D2] p-7 lg:p-8 hover:border-[#F26B1A]/40 transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-22px_rgba(31,29,26,0.18)]">
            <MessageCircle className="w-8 h-8 text-[#F26B1A] mb-5" strokeWidth={1.8} />
            <h3 className="font-display text-[#1F1D1A] text-[24px]">{labels.wa}</h3>
            <p className="mt-2 text-[#5C5852] text-[14px]">{lang === 'de' ? 'Schnellste Antwort — meist binnen Minuten.' : 'Snelste reactie — meestal binnen minuten.'}</p>
            <span className="block mt-4 text-[14px] font-semibold text-[#F26B1A]">{phone}</span>
          </a>

          <a href={telHref(phone)}
            className="group block rounded-[22px] bg-white border border-[#E5E0D2] p-7 lg:p-8 hover:border-[#F26B1A]/40 transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-22px_rgba(31,29,26,0.18)]">
            <Phone className="w-8 h-8 text-[#F26B1A] mb-5" strokeWidth={1.8} />
            <h3 className="font-display text-[#1F1D1A] text-[24px]">{labels.call}</h3>
            <p className="mt-2 text-[#5C5852] text-[14px]">{lang === 'de' ? 'Direkt mit dem Inhaber. Kein Callcenter.' : 'Direct contact met de eigenaar. Geen callcenter.'}</p>
            <span className="block mt-4 text-[14px] font-semibold text-[#F26B1A]">{phone}</span>
          </a>

          <a href={`mailto:${company.email}`}
            className="group block rounded-[22px] bg-white border border-[#E5E0D2] p-7 lg:p-8 hover:border-[#F26B1A]/40 transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-22px_rgba(31,29,26,0.18)]">
            <Mail className="w-8 h-8 text-[#F26B1A] mb-5" strokeWidth={1.8} />
            <h3 className="font-display text-[#1F1D1A] text-[24px]">{labels.email}</h3>
            <p className="mt-2 text-[#5C5852] text-[14px]">{lang === 'de' ? 'Antwort innerhalb von 1 Werktag.' : 'Reactie binnen 1 werkdag.'}</p>
            <span className="block mt-4 text-[14px] font-semibold text-[#F26B1A]">{company.email}</span>
          </a>
        </div>
      </section>

      <section className="bg-[#FAF8F2] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-y-12 gap-x-16">
          <div>
            <h2 className="text-[11.5px] uppercase tracking-[0.22em] text-[#F26B1A] font-semibold mb-5">{labels.address}</h2>
            <p className="flex items-start gap-3 text-[15px] text-[#1F1D1A] leading-relaxed">
              <MapPin className="w-5 h-5 text-[#F26B1A] mt-0.5 shrink-0" />
              <span>
                <strong>{company.name}</strong><br />
                {company.street}<br />
                {company.postal} {company.city}<br />
                {lang === 'de' ? 'Niederlande' : 'Nederland'}<br />
                <span className="text-[#5C5852] text-[13px] mt-2 inline-block">KvK {company.kvk}</span>
              </span>
            </p>
          </div>
          <div>
            <h2 className="text-[11.5px] uppercase tracking-[0.22em] text-[#F26B1A] font-semibold mb-5">{labels.hours}</h2>
            <ul className="space-y-2 text-[15px] text-[#1F1D1A]">
              <li className="flex items-center gap-3"><Clock className="w-4 h-4 text-[#F26B1A]" /> {hrs.weekdays}</li>
              <li className="flex items-center gap-3"><Clock className="w-4 h-4 text-[#F26B1A]" /> {hrs.saturday}</li>
              <li className="flex items-center gap-3"><Clock className="w-4 h-4 text-[#F26B1A]/40" /> {hrs.sunday}</li>
            </ul>
          </div>
        </div>
      </section>
    </article>
  );
}
